import React, { useState, useEffect } from 'react';
import { Modal, Typography, Select, message } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { addUser, getListAction } from 'store/pageSlice';
import { getTripListActionV2 } from 'store/tripSliceV2';
import { getUIVersion } from 'utils/router-static-data';

const reasonOptions = [
	{ label: 'Select Reason', value: '' },
	{ label: 'Wrong Data', value: '1' },
	{ label: 'Client Disagreed', value: '2' },
	{ label: 'Vehicle Not Reported', value: '3' },
];

// eslint-disable-next-line sonarjs/cognitive-complexity
const transformTripToPayload = (trip, selectedReason, tripId) => {
	const basePayload = {
		id: tripId,
		trip_type: trip?.trip_type,
		city: trip?.city,
		project_id: trip?.project_id,
		vendor_id: trip?.vendor_id,
		vehicle_number: trip?.vehicle_number,
		vehicle_type: trip?.vehicle_type,
		reporting_date: trip?.reporting_date
			? trip.reporting_date.slice(0, 10)
			: trip?.info?.reporting_date?.slice(0, 10),
		reporting_time: (trip?.reporting_time || trip?.info?.reporting_time)?.slice(0, 16),
		...(trip?.driver_id && { driver_id: trip?.driver_id }),
		is_valid: false,
		is_invalid_reason: parseInt(selectedReason, 10),
		info: trip?.info,
		...(trip?.source_pincode && { source_pincode: trip?.source_pincode }),
		...(trip?.destination_pincode && { destination_pincode: trip?.destination_pincode }),
	};

	if (basePayload.trip_type === 'replacement') {
		return {
			...basePayload,
			prev_trip_id: trip?.lrId,
		};
	}

	return basePayload;
};

const buildV2InvalidTripPayload = (selectedReason, tripId) => ({
	id: tripId,
	is_valid: false,
	is_valid_trip: false,
	is_invalid_reason: parseInt(selectedReason, 10),
	invalid_reason_id: parseInt(selectedReason, 10),
});

const resolveTripId = (tripId, tripDetails) =>
	tripId || tripDetails?.id || tripDetails?.trip_id || null;

function InvalidTripModal({ open, onClose, tripId, onSuccess }) {
	const [selectedReason, setSelectedReason] = useState(null);
	const [tripDetails, setTripDetails] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const dispatch = useDispatch();
	const isV2UI = getUIVersion() === 'new';

	useEffect(() => {
		if (open && tripId) {
			const action = isV2UI
				? getTripListActionV2({ search_type: 'trip', id: tripId, limit: 1, offset: 0 })
				: getListAction({ search_type: 'trip', id: tripId, filterflag: false });

			dispatch(action).then((res) => {
				setTripDetails(res?.rows?.[0] || res?.data?.[0] || res?.payload?.rows?.[0] || null);
			});
		} else if (!open) {
			setSelectedReason(null);
			setTripDetails(null);
			setSubmitting(false);
		}
	}, [open, tripId, dispatch, isV2UI]);

	const handleOk = async () => {
		const validTripId = resolveTripId(tripId, tripDetails);
		if (!selectedReason) {
			message.error('Please select a reason');
			return;
		}
		if (!validTripId || validTripId === '-') {
			message.error('Trip ID not found');
			return;
		}

		if (!isV2UI && !tripDetails) {
			message.error('Trip details are still loading. Please try again.');
			return;
		}

		setSubmitting(true);
		try {
			const payload = isV2UI
				? buildV2InvalidTripPayload(selectedReason, validTripId)
				: transformTripToPayload(tripDetails, selectedReason, validTripId);
			const res = await dispatch(
				addUser({
					data: {
						update_type: isV2UI ? 'invalidate_trip' : 'trip',
						data: payload,
					},
					isEditMode: true,
				})
			);

			if (res?.error) {
				throw new Error(res.error.message || 'Failed to mark trip as invalid');
			}

			message.success('Trip marked as invalid');
			setSelectedReason(null);
			onSuccess?.();
			onClose();
		} catch (error) {
			message.error(error?.message || 'Failed to mark trip as invalid');
		} finally {
			setSubmitting(false);
		}
	};

	const handleCancel = () => {
		setSelectedReason(null);
		onClose();
	};

	return (
		<Modal
			open={open}
			onCancel={handleCancel}
			onOk={handleOk}
			okButtonProps={{
				loading: submitting,
				disabled: !selectedReason || selectedReason === '',
				style: { backgroundColor: '#18405D', borderColor: '#18405D' },
			}}
			cancelButtonProps={{ disabled: submitting }}
			title="Invalid Trip"
			style={{ width: 400 }}
			okText="Confirm"
		>
			<Typography.Text>
				Are you sure you want to mark trip #{tripId} as invalid?
			</Typography.Text>

			<div style={{ marginTop: 12, width: '100%' }}>
				<Select
					style={{ width: '100%', marginTop: 12 }}
					placeholder="Select Reason"
					value={selectedReason}
					onChange={(value) => setSelectedReason(value)}
					options={reasonOptions}
				/>
			</div>
		</Modal>
	);
}

InvalidTripModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	tripId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	onSuccess: PropTypes.func,
};

export default InvalidTripModal;
